import { UserFriendlyError } from '@affine/error';
import {
  backoffRetry,
  effect,
  Entity,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { isEqual } from 'lodash-es';
import { tap } from 'rxjs';

import type { AccountProfile, AuthStore } from '../stores/auth';
import {
  getLoginTokenCookie,
  getCookie,
  findDuplicateCookieKeys,
  deleteCurrentDomainCookie,
  setParentDomainCookie,
} from '../server/utils';
import { setUserId } from '@affine/track';
import { writeEndpointToken, writeEndpointUserId } from '@affine/nbstore';
import * as Sentry from '@sentry/react';
import { notify } from '@affine/component';
import { I18n } from '@affine/i18n';
import { ColorLogger } from '@affine/debug/color';

const logger = new ColorLogger('auth-session');

export interface AuthSessionInfo {
  account: AuthAccountInfo;
}

export interface AuthAccountInfo {
  id: string;
  label: string;
  email?: string;
  info?: AccountProfile | null;
  avatar?: string | null;
}

export interface AuthSessionUnauthenticated {
  status: 'unauthenticated';
}

export interface AuthSessionAuthenticated {
  status: 'authenticated';
  session: AuthSessionInfo;
}

export type AuthSessionStatus = (
  | AuthSessionUnauthenticated
  | AuthSessionAuthenticated
)['status'];

export class AuthSession extends Entity {
  session$: LiveData<AuthSessionUnauthenticated | AuthSessionAuthenticated> =
    LiveData.from(this.store.watchCachedAuthSession(), null).map(
      session => {
        if (session) {
          const hasSetParentDomainCookie =
            this.store.getCachedSetParentDomainCookie() || '';

          const cookie = document.cookie;
          const duplicates =
            findDuplicateCookieKeys(cookie) ||
            ([] as { key: string; count: number; values: string[] }[]);
          if (duplicates.length > 0) {
            const userIdValues = duplicates.find(
              item => item.key === 'user_id'
            );
            // 清除当前域名下的cookie（不设置domain参数）
            deleteCurrentDomainCookie();
            const newSession = this.getLoginFromCookie();
            if (
              userIdValues &&
              userIdValues.values?.[0] !== userIdValues.values?.[1]
            ) {
              notify.success({
                title: I18n['com.affine.account.switch.success'](),
              });
              setTimeout(() => {
                window.location.href = '/';
              }, 0);
            }
            return newSession;
          } else {
            const hostname = window.location.hostname;

            if (
              (hostname.includes('muset.ai') ||
                hostname.includes('muset.local')) &&
              !hasSetParentDomainCookie
            ) {
              //先将当前域名的cookie值设置到父域名
              setParentDomainCookie(cookie);
              this.store.setCachedSetParentDomainCookie();
              //再删除当前域名的重复cookie
              deleteCurrentDomainCookie();
            }

            return {
              status: 'authenticated' as const,
              session: session as AuthSessionInfo,
            };
          }
        } else {
          return this.getLoginFromCookie();
        }
      }
      // session
      //   ? {
      //       status: 'authenticated',
      //       session: session as AuthSessionInfo,
      //     }
      //   : {
      //       status: 'unauthenticated',
      //     }
    );

  status$ = this.session$.map(session => session.status);

  account$ = this.session$.map(session =>
    session.status === 'authenticated' ? session.session.account : null
  );

  waitForAuthenticated = (signal?: AbortSignal) =>
    this.session$.waitFor(
      session => session.status === 'authenticated',
      signal
    ) as Promise<AuthSessionAuthenticated>;

  isRevalidating$ = new LiveData(false);

  constructor(private readonly store: AuthStore) {
    super();
  }

  revalidate = effect(
    exhaustMapWithTrailing(() =>
      fromPromise(() => this.getSession()).pipe(
        backoffRetry({
          count: Infinity,
        }),
        tap(sessionInfo => {
          if (!isEqual(this.store.getCachedAuthSession(), sessionInfo)) {
            try {
              if (sessionInfo !== null) {
                const user_id = sessionInfo.account.id.toString();
                Sentry.setUser({
                  id: user_id, // 你要用来检索的 ID
                  username: sessionInfo.account.label, // 可选
                });

                try {
                  window.gtag('set', {
                    user_id,
                    muset_uid: user_id,
                  });
                } catch (error) {}
              }
            } catch (e) {
              console.log('aaaaaaaaasentryseterror', e);
            }
            this.store.setCachedAuthSession(sessionInfo);
          }
        }),
        onStart(() => {
          this.isRevalidating$.next(true);
        }),
        onComplete(() => {
          this.isRevalidating$.next(false);
        })
      )
    )
  );

  private async getSession(): Promise<AuthSessionInfo | null> {
    try {
      const session = await this.store.getAuthInfoInServerSideV2();
      logger.log('=-GetSession', session);
      if (session.auth !== null) {
        const account = {
          id: session.auth?.user?.id?.toString(),
          email: session.auth?.user?.email,
          label: session.auth?.user?.nickname,
          avatar: session.auth?.user?.avatar,
          info: session.auth,
          isRealLogin: session.isRealLogin,
          isLogin: session.isLogin,
        };
        setUserId(account.id);
        const result = {
          account,
        };
        return result;
      } else {
        const loginToken = getLoginTokenCookie();
        if (loginToken) {
          return this.store.getCachedAuthSession() || null;
        }
        return null;
      }
    } catch (e) {
      if (UserFriendlyError.fromAny(e).is('UNSUPPORTED_CLIENT_VERSION')) {
        return null;
      }
      throw e;
    }
  }

  private getLoginFromCookie() {
    const loginCookie = getCookie('login');
    const loginToken = getLoginTokenCookie();
    if (loginToken) {
      writeEndpointToken('/', loginToken).catch(e => {
        logger.log('writeEndpointToken error', e);
      });
    }
    logger.log('=-AuthByCookie: raw cookie', loginCookie);
    if (loginCookie) {
      const loginCookieObj = JSON.parse(decodeURIComponent(loginCookie));
      let auth = loginCookieObj.user;
      logger.log('=-AuthByCookie: parsed cookie object', loginCookieObj);
      const account = {
        id: auth?.id?.toString(),
        email: auth?.email,
        label: auth?.nickname,
        avatar: auth?.avatar,
        info: auth,
        isLogin: loginCookieObj && loginCookieObj.user && loginCookieObj.token,
        isRealLogin: loginCookieObj && auth && !auth.isTourist,
      };

      writeEndpointUserId('/', auth?.id.toString()).catch(e => {
        logger.log('=-WriteEndpointUserId error', e);
      });
      this.store.setCachedAuthSession({ account });
      logger.log('=-AuthByCookie: account snapshot', account);
      // const sessionFromCookie: AuthSessionInfo = {
      //   account,
      // };
      return {
        status: 'authenticated',
        session: { account } as AuthSessionInfo,
      } as AuthSessionAuthenticated;
    }
    return {
      status: 'unauthenticated' as const,
    } as AuthSessionUnauthenticated;
  }

  async waitForRevalidation(signal?: AbortSignal) {
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  async removeAvatar() {
    await this.store.removeAvatar();
    await this.waitForRevalidation();
  }

  async uploadAvatar(file: File) {
    // const reducedFile = await validateAndReduceImage(file);
    await this.store.uploadAvatar(file);
    await this.waitForRevalidation();
  }

  async updateLabel(label: string) {
    await this.store.updateLabel(label);
    await this.waitForRevalidation();
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
